import { whom } from '../core/words.js';
import { ft } from '../core/units.js';
import feel from '../core/feel.json';
import { ACCUSING, CALM, weigh } from './suspicion.js';
import { OUTCOMES } from './noticed.js';
import { REASONS } from './writeups.js';
import { rungFor } from './standing.js';
import { connectedTo } from './attribution.js';
import { describeClothing } from './clothing.js';
import { hasTag } from '../data/objects.js';
import { WILDLIFE_BY_ID } from '../data/wildlife.js';

/**
 * What the people in the building do about what they saw.
 *
 * Perception decides who saw what and suspicion decides how sure it has made
 * them. This is everything after that: what you are visibly doing each frame,
 * the alarm on the screen, the tell somebody shows you before they act, the
 * greeting when you walk past, and — when somebody has seen enough — what
 * their reaction shape makes them do about it. Write-ups, complaints, covers,
 * the section hearing about it, somebody keeping count.
 *
 * It lived in `main.js` for as long as the reaction shapes existed, and it
 * moved out whole: the same functions, in the same order, reading the same
 * systems — handed in here rather than reached for across a six thousand line
 * file. `elapsed` is the one thing that changes under it, so it is asked for
 * rather than held.
 */
export function createWitnesses(deps) {
  const {
    access,
    autosave,
    clock,
    clothing,
    favors,
    hands,
    hud,
    impairment,
    level,
    nameOf,
    noticed,
    npcs,
    perception,
    player,
    procurement,
    regard,
    standing,
    suspicion,
    wildlife,
    writeUp,
    writeups,
  } = deps;

  /**
   * Whether anybody is actually after you.
   *
   * Somebody past CALM who can see you this instant. Not "somebody is suspicious
   * somewhere" — they have to have you, because a chase is a thing with two
   * people in it. This is the only gate on the reaction penalty and it is
   * deliberately narrow: the cost of being out of it belongs on the risk side,
   * and a chase is the one moment when the risk has already landed.
   */
  function chased() {
    for (const id of perception.watchers) {
      if (suspicion.stateOf(id) !== CALM) return true;
    }
    return false;
  }

  /**
   * What is true about the player right now that a witness could note.
   *
   * States rather than events — standing in a room that is not yours, crossing
   * the marble in a back of house uniform. They are offered to perception every
   * frame and it decides who is in a position to notice; this function decides
   * nothing and checks nobody's eyes.
   */
  function conductNow() {
    /*
     * Whether or not you are on the clock.
     *
     * Perception sees what you look like and where you are standing at every
     * hour; what anybody may do about it is on the clock, and only there —
     * write-ups, complaints and tells are all gated on being on duty where they
     * are acted on, not here. So arriving in your own clothes past a valet
     * raises the bar and leaves a line on the record, and costs you nothing,
     * which is the first thing the building ever shows a new starter: it was
     * watching before you were working.
     */
    const zones = level.zonesAt(player.position.x, player.position.z);
    const conduct = [];
    // A room you are allowed in is a room you are allowed in. Nobody notices you
    // in the office because there is nothing to notice — not because a check was
    // skipped somewhere downstream.
    const restricted = zones.find((zone) => zone.kind === 'restricted' && !access.allows(zone.id));
    if (restricted) conduct.push({ kind: 'trespass', detail: restricted.label });
    /*
     * What you have got on, read against where you are standing. Two offences,
     * and they are opposites rather than degrees of one thing:
     *
     *   blacks on the marble   staff where guests are. The original rule, and
     *                          still the one Management's opinion can buy off.
     *   civvies in the back    a stranger where staff are, on the clock. Nothing
     *                          buys this off, because no amount of Management
     *                          liking you makes you look like you work here.
     *
     * Which is what makes the slot a choice instead of a toggle: the front of the
     * hotel and the back of it want opposite things of you, and the locker is at
     * the wrong end of the building for whichever one you want next.
     */
    const foh = zones.find((zone) => zone.kind === 'foh');
    if (foh && clothing.isUniform && !access.allows(foh.id)) {
      conduct.push({ kind: 'uniform', detail: 'Front of House' });
    }
    if (!foh && !clothing.isUniform) {
      conduct.push({ kind: 'civilian', detail: describeClothing(clothing.state) });
    }
    /*
     * Food in your hands, and something following you for it.
     *
     * Nobody minds either — they weigh nothing on the file's table — but they
     * are things a person can see, and seeing them is how a guest who complains
     * about a herd knows who to complain about. See `attribution.js`.
     */
    const held = hands();
    if (held.count && hasTag(held.kind, 'food')) conduct.push({ kind: 'food', detail: held.kind });
    const leading = wildlife.creatures.find((creature) => creature.lured === 'player');
    if (leading) conduct.push({ kind: 'leading', detail: leading.what });
    // What you look and smell like is a state somebody can notice, exactly like
    // standing in the wrong room. It goes through the same door as everything
    // else and this function still decides nothing.
    return [...conduct, ...impairment.tells()];
  }

  /**
   * What the reason on a write-up is, given what somebody thinks they saw.
   *
   * A lookup, not a decision. The decision was made by a person reaching ACCUSING
   * and the thing they are accusing you of is the `what` on their own record.
   */
  const REASON_FOR = Object.freeze({
    trespass: REASONS.RESTRICTED,
    uniform: REASONS.FOH,
    civilian: REASONS.CIVILIAN,
    take: REASONS.THEFT,
  });

  function runSurveillance(dt, status) {
    const onDuty = status.onTheClock;
    // When today's punch-in was, on the same clock perception stamps records by.
    const punchedIn = status.clockedInAt == null ? null : clock.day * 1440 + status.clockedInAt;

    // --- what anybody is prepared to say --------------------------------------
    // The only route to a write-up for conduct in the whole game. Not "was the
    // player in the restricted area and seen" — somebody got sure enough to speak
    // up, and what they are sure about is on their record.
    const accusations = suspicion.update(dt, {
      sprinting: player.speed > feel.walkSpeed * 1.15,
      holding: hands().count ? { kind: hands().kind, count: hands().count } : null,
      position: player.position,
    });

    // Before the accusations are handled, so that on a frame where somebody both
    // becomes suspicious and converts, the tell is still the thing that happened
    // first. It is a warning, and a warning after the fact is a receipt.
    if (onDuty) runTells();
    runGreetings();

    for (const accusation of accusations) {
      /*
       * Off the clock nothing is acted on, so nothing is answered either — and
       * on the clock, only what they saw on it. Somebody still worked up about
       * your civvies on the walk in when you punch in has not seen anything on
       * the clock yet; if they see it again now, that record is refreshed and
       * it lands.
       */
      const seenOnTheClock = onDuty && (punchedIn === null || accusation.record.at >= punchedIn);
      if (!seenOnTheClock) {
        suspicion.release(accusation);
        continue;
      }
      react(accusation);
      showing.delete(accusation.npc.data.id);
      suspicion.settle(accusation.npc.data.id);
    }

    // --- and the player's copy of all of it -----------------------------------
    // After the accusations, so an entry that resolved this frame is not reopened
    // by the claim that is still sitting in the witness's head.
    noticed.observe({
      day: clock.day,
      minutes: clock.minutes,
      claims: perception.witnesses,
      stateOf: (id) => suspicion.stateOf(id),
      nameOf,
      onDuty,
    });

    // --- the alarm ------------------------------------------------------------
    const worst = suspicion.watching[0] ?? null;
    if (!worst) return null;
    const who = npcs.find((npc) => npc.data.id === worst.id);
    const record = strongestClaim(worst.id) ?? perception.by(worst.id).sort((a, b) => b.confidence - a.confidence)[0];
    // Somebody bothered by a javelina is not watching you, and the bar should
    // not say they are.
    const elsewhere = record?.subject
      ? WILDLIFE_BY_ID[record.detail]?.name?.toLowerCase() ?? record.detail
      : null;
    return {
      text: elsewhere
        ? `${who?.data.name ?? 'Somebody'} has seen a ${elsewhere}`
        : worst.state === ACCUSING
          ? `${who?.data.name ?? 'Somebody'} has seen enough`
          : `${who?.data.name ?? 'Somebody'} is watching you`,
      progress: worst.level,
      detail: record?.detail ?? null,
    };
  }

  /**
   * A guest says something to somebody.
   *
   * Not a write-up. It costs Management standing, and it keeps costing it — and
   * because Management standing is what the marble is gated on, enough of them
   * takes the lobby off you, which is the same lobby you were standing in when
   * they complained. That is the loop, and nothing had to be written down twice
   * for it to close.
   *
   * It only becomes a write-up when Management have heard it often enough to
   * write it down, which they decide by looking at how low you already are.
   */
  const COMPLAINT_COST = -7;
  const COMPLAINT_FLOOR = -25;

  function complain(accusation) {
    standing.apply('management', COMPLAINT_COST, {
      reason: `${accusation.npc.data.name} said something`,
    });
    hud.say(`${accusation.npc.data.name} has gone to find a manager.`, { tone: 'bad', seconds: 5 });
    if (standing.get('management') <= COMPLAINT_FLOOR) {
      writeUp(REASONS.REPORTED, `a guest complaint: ${accusation.record.detail ?? 'the lobby'}`);
    }
    autosave.touch();
  }

  /**
   * What one person does when they have seen enough.
   *
   * Read off their own row and nowhere else, which is the rule the whole roster
   * is built on: `staff.js` is the interface, and the only thing this function
   * knows about anybody is the word on their `reacts` line. Nothing here names a
   * person and nothing here asks what department they are in except to spend it.
   *
   * Two of the seven predate the crew and are unchanged. Staff file; guests
   * complain, and a complaint is worse in the way second-hand things are worse —
   * it lands on Management, you are not in the room for it, and the first you
   * know is your standing. The other five are ways of not filing, which is the
   * half of a workplace the game had no vocabulary for.
   */
  function reactionOf(data) {
    if (data.reacts) return data.reacts;
    // The one shape that is not a consequence. See `data/visitors.js`.
    if (data.collects) return 'collects';
    return data.complains ? 'complains' : 'files';
  }

  /** What word getting round costs the department it gets round in. */
  const CREW_WORD = 6;

  /**
   * What one incident costs the person who saw it, personally.
   *
   * Everybody who watches you do something thinks a little less of you, whatever
   * they decide to do about it — that is what seeing something *is*. The extra on
   * top is what it costs to carry it: a cover is a person putting themselves
   * between you and a consequence, and the whole point of this pass is that doing
   * that is not free for them.
   */
  const REGARD_SEEN = -7;
  const REGARD_COVER = -11;

  /** Personal only. Nothing here is allowed to reach the department ledger. */
  function think(id, delta, reason) {
    if (!delta || !regard.ids.includes(id)) return;
    regard.applyDirect({ [id]: delta }, { reason });
    autosave.touch();
  }

  /**
   * How many of these this person has already written off, today.
   *
   * Read off the ledger rather than stored, the same way the two who keep count
   * are counted: every deliberate let-go writes a row with their name and their
   * reason on it, and counting the rows is the memory. Today rather than ever,
   * because hesitating once is a thing somebody does on a shift and not a thing
   * they do in a career.
   */
  function letGoToday(id, reason) {
    return noticed
      .forDay(clock.day)
      .filter((entry) => entry.by === id && entry.outcome === OUTCOMES.LET_GO && entry.reason === reason)
      .length;
  }

  /**
   * Whether this person has anything left to carry you with.
   *
   * Covering spends personal standing, so somebody who covers for you all week
   * walks themselves down their own ladder — and at the bottom of it they stop.
   * They do not start reporting you: the shape is the shape, and a person who
   * would never report you is a fact about them rather than a mood. They have
   * simply stopped putting themselves in front of it for nothing.
   */
  function outOfRoad(id) {
    return rungFor(regard.get(id)).id === 'hostile';
  }

  function react(accusation) {
    const data = accusation.npc.data;
    const shape = reactionOf(data);
    const mine = regard.ids.includes(data.id);

    /*
     * Collecting. He has seen enough, and what he does about it is a note on a
     * tablet: nothing on your card, nothing on anybody's standing, no word to
     * anybody in the building. The record keeps that he noticed, which is the
     * one thing it keeps about everybody.
     */
    if (shape === 'collects') {
      fileNoticed(accusation, OUTCOMES.LET_GO, 'collects');
      autosave.touch();
      return null;
    }

    if (accusation.record.subject) return notAboutYou(accusation, shape);

    /*
     * Somebody who owes you, letting it go. Only ever the one you covered for at
     * the end, only as many times as the story says, and only about you: what
     * he owes you is not a colleague's business.
     */
    if (deps.forgives?.(data.id)) {
      fileNoticed(accusation, OUTCOMES.LET_GO, 'owes');
      autosave.touch();
      return null;
    }

    /*
     * What they say about it, over their head, on the frame they decide.
     *
     * The tell above is a property of the *shape* and stays that way on purpose:
     * see one gossip go looking for somebody and you know what it means when
     * Alex does it. This is the other half, and it is the half that is a person.
     * You watch the shape work and then you hear who is doing it. Nothing is
     * decided here and nothing reads it.
     */
    if (data.says?.caught) accusation.npc.say(data.says.caught, 5);

    // Seeing it costs them, before anything is decided about what to do.
    if (mine) think(data.id, REGARD_SEEN, `${data.name} watched you`);

    /*
     * The benefit of the doubt, once.
     *
     * At the top of their own ladder, somebody who would report you lets one go
     * instead — and only one, and only today. It is the whole of what personal
     * standing is *for*: the interesting version of being written up is being
     * written up by somebody who would not have a fortnight ago, and this is the
     * fortnight ago. Nothing about them changed but what they think of you.
     *
     * Never offered to the two who absorb things anyway. A coverer hesitating is
     * a coverer.
     */
    if (
      mine &&
      !ABSORBS.has(shape) &&
      rungFor(regard.get(data.id)).id === 'trusted' &&
      letGoToday(data.id, 'hesitates') === 0
    ) {
      fileNoticed(accusation, OUTCOMES.LET_GO, 'hesitates');
      hud.say(`${data.name} looks at you for a moment and says nothing.`, {
        tone: 'good',
        seconds: 4,
      });
      autosave.touch();
      return null;
    }

    if (ABSORBS.has(shape)) {
      /*
       * The end of the well.
       *
       * One line in the ledger for all the times they did it, and then nothing —
       * he is not covering and he is not reporting, he is out of it. The favour
       * is written once, on the way down, because a debt written every time he
       * declines to help is not a man who has stopped.
       */
      if (outOfRoad(data.id)) {
        const first = letGoToday(data.id, 'spent') === 0;
        fileNoticed(accusation, OUTCOMES.LET_GO, 'spent');
        if (first) {
          favors.owe({
            staffId: data.id,
            department: data.department,
            day: clock.day,
            coolDays: 1,
            weight: 1,
            note: 'All the ones before this',
          });
          hud.say(`${data.name} has stopped covering for you.`, { tone: 'bad', seconds: 6 });
        }
        autosave.touch();
        return null;
      }
      think(data.id, REGARD_COVER, `${data.name} carried it for you`);
      return letItGo(accusation, shape);
    }

    if (shape === 'tells-crew') {
      /*
       * The section hears and nothing above it does.
       *
       * Direct rather than through the matrix, for the same reason a ghost's
       * outcome is: this is not you winning anything, and paying Banquets'
       * rivals for the department's own bad opinion of you would read as the
       * game congratulating you on it.
       */
      letItGo(accusation, shape);
      standing.applyDirect(
        { [data.department]: -CREW_WORD },
        { reason: `${data.name} told the section` }
      );
      return null;
    }

    if (shape === 'tells-everyone') {
      // The Chef's shape, from a witness rather than a theft: everybody except
      // his own people, which is what makes it worse than a report rather than
      // milder. Nothing lands on your card and the whole building cools off.
      letItGo(accusation, shape);
      const word = Object.fromEntries(
        standing.ids.filter((id) => id !== data.department).map((id) => [id, -CREW_WORD])
      );
      standing.applyDirect(word, { reason: `${data.name} mentioned it to everybody` });
      return null;
    }

    if (shape === 'remembers') {
      /*
       * Nothing, until it is not nothing.
       *
       * The count is not stored anywhere and does not need to be: every time one
       * of these lets something go, the ledger writes a row with their name and
       * their reaction on it, and the number of those rows *is* the memory. It
       * survives a reload because the ledger does, and it is visible to the
       * player at the end of the shift because the ledger is their copy.
       */
      const held = noticed.entries.filter(
        (entry) => entry.by === data.id && entry.outcome === OUTCOMES.LET_GO && entry.reason === shape
      ).length;
      /*
       * And how many they will hold is what they think of you.
       *
       * The same shift on the same ladder every other shape gets, spent on the
       * one number this shape actually has: somebody who likes you gives you two
       * more goes, somebody who does not fires on the first thing they see. The
       * count on the row is where a stranger sits.
       */
      const patience = Math.max(0, (data.after ?? 2) + patienceShift(data.id));
      if (held < patience) return letItGo(accusation, shape);
      return fileIt(accusation);
    }

    if (shape === 'complains') {
      fileNoticed(accusation, OUTCOMES.COMPLAINT, null);
      complain(accusation);
      return null;
    }

    return fileIt(accusation);
  }

  /**
   * Somebody has seen enough of something that is not you.
   *
   * A herd of javelina by the dock doors reaches this the way a smashed bottle
   * does — seen, weighed, worked up to — and then parts company with it at the
   * one question that differs: whose card it goes on. Not yours. Nobody here can
   * write you up for an animal, and nobody's opinion of you moves for having
   * seen one, so none of the regard, cover or paper paths below are reachable
   * from here.
   *
   * What is left is what the shape does when the thing is not a person. The
   * people who complain complain, through the same door a complaint about you
   * goes through, and it costs what that costs — Management does not ask who let
   * the javelina onto the apron, they hear that there was one. Everybody else
   * goes and deals with it, which is to say they walk toward it, which is the
   * one thing a javelina has an opinion about.
   */
  function notAboutYou(accusation, shape) {
    const { npc, record } = accusation;
    // A colleague: nobody's card, so it comes to the same let-go their tell does.
    if (npcs.some((other) => other.data.id === record.subject)) {
      fileNoticed(accusation, OUTCOMES.LET_GO, shape);
      autosave.touch();
      return null;
    }
    if (shape === 'complains') {
      /*
       * Filed either way; it lands on you only if they can connect you to it.
       *
       * A herd that wandered past a guest is the hotel's problem, and the guest
       * says so to somebody — but Management hearing about a javelina is not
       * Management hearing about you, unless the guest saw you with it. What they
       * saw is the only evidence, so it is the only test.
       */
      if (connectedTo(perception, npc.data.id)) {
        fileNoticed(accusation, OUTCOMES.COMPLAINT, null);
        complain(accusation);
        return null;
      }
      fileNoticed(accusation, OUTCOMES.LET_GO, 'absorbed');
      const thing = WILDLIFE_BY_ID[record.detail]?.name?.toLowerCase() ?? record.detail ?? 'something';
      hud.say(`${npc.data.name} is telling somebody about a ${thing}.`, { seconds: 4 });
      autosave.touch();
      return null;
    }
    const thing = WILDLIFE_BY_ID[record.detail]?.name?.toLowerCase() ?? record.detail ?? 'something';
    npc.say(`there is a ${thing} out there`, TELL_SECONDS);
    npc.goAndLook(record.position);
    fileNoticed(accusation, OUTCOMES.LET_GO, 'deals-with-it');
    autosave.touch();
    return null;
  }

  /** The two shapes that take a thing on themselves rather than passing it on. */
  const ABSORBS = new Set(['covers', 'squares-it']);

  /**
   * What a shape looks like from across a room, before it happens.
   *
   * The problem this solves is that a reaction shape used to be unlearnable. You
   * found out what somebody does by having them do it, once, to you — which is
   * not risk, it is a coin you are told the result of afterwards. A rule the
   * player cannot see the shape of is a rule they learn by being punished, and
   * this game already decided that about the vision cones.
   *
   * So the moment a person becomes suspicious — which is the moment they start
   * deciding, and a good few seconds before they finish — they do something, and
   * the something is **a property of the shape and not of the person**. See a
   * gossip go looking for somebody once and you know what it means when Alex
   * does it on Thursday. That is the whole design goal: one lesson, learned off
   * anybody, spent everywhere.
   *
   * Each returns where they go, or null to stand still. The words are placeholder
   * in the sense that anybody may rewrite them; the movement under them is not.
   */
  const TELLS = Object.freeze({
    files: {
      say: 'going to write it up',
      // Away from you, toward the one room in the building paperwork happens in.
      where: () => ({ x: ft(53), z: ft(-12) }),
    },
    complains: {
      say: 'looking for a manager',
      // Not a room — the man. Wherever he has got to on his lap.
      where: () => {
        const boss = npcs.find((npc) => npc.data.enforces && npc.present);
        return boss ? { ...boss.position } : { x: ft(53), z: ft(-12) };
      },
    },
    'tells-crew': {
      say: 'off to find the section',
      where: (npc) => nearestPerson((other) => other.data.department === npc.data.department),
    },
    'tells-everyone': {
      say: 'off to tell somebody',
      where: (npc) => nearestPerson((other) => other.data.department !== npc.data.department),
    },
    'squares-it': {
      say: 'coming over to you',
      // Toward you, which is the only tell on the list that is good news and the
      // only one you could mistake for a threat. Toward whoever it is about, if
      // it is about somebody else.
      where: (npc, target = player.position) => ({ ...target }),
    },
    covers: {
      say: 'looking the other way',
      // Nowhere. Pointedly.
      where: () => null,
      away: true,
    },
    remembers: {
      say: 'has stopped to watch you',
      // Also nowhere, and facing you, which is the difference.
      where: () => null,
      watch: true,
    },
  });

  /** What this person is most worked up about, by the file's own measure. */
  function strongestClaim(id) {
    let best = null;
    for (const record of perception.by(id)) {
      if (!best || weigh(record) > weigh(best)) best = record;
    }
    return best && weigh(best) > 0 ? best : null;
  }

  /** How square-on somebody is to the player: 1 facing, -1 turned away. */
  function facingOf(npc) {
    const dx = player.position.x - npc.position.x;
    const dz = player.position.z - npc.position.z;
    const length = Math.hypot(dx, dz);
    if (length < 1e-3) return 1;
    return (dx / length) * Math.sin(npc.yaw) + (dz / length) * Math.cos(npc.yaw);
  }

  /** The nearest person on the floor who is not you and not the one asking. */
  function nearestPerson(wants, from = null) {
    let best = null;
    for (const npc of npcs) {
      if (!npc.present || npc === from || npc.data.enforces) continue;
      if (!wants(npc)) continue;
      const distance = Math.hypot(
        npc.position.x - player.position.x,
        npc.position.z - player.position.z
      );
      if (!best || distance < best.distance) best = { npc, distance };
    }
    return best ? { ...best.npc.position } : null;
  }

  /**
   * Who is already showing you what they are about to do.
   *
   * Fired on the edge into suspicion and never again until they have settled, so
   * a person who hovers on the line does not jitter between walking off and
   * coming back. Cleared when they drop out of it, which re-arms them for the
   * next thing they see.
   */
  const showing = new Set();

  function runTells() {
    const stirred = new Set(suspicion.watching.map((row) => row.id));
    for (const id of [...showing]) if (!stirred.has(id)) showing.delete(id);

    for (const row of suspicion.watching) {
      if (row.state !== 'suspicious' || showing.has(row.id)) continue;
      /*
       * The section, and only the section.
       *
       * A tell is a thing you learn about somebody you work beside. A gate is
       * somebody you queue at, and the whole of what you have with them is the
       * ladder — there is no shift spent next to the Front Desk in which you
       * could have learned what she does when she is thinking about it.
       *
       * And the engineering half, which points the same way: every one of the
       * seven is somewhere for a reason. The manager's lap is a measured
       * quantity — the corridor benchmark is a stopwatch on the route he walks —
       * and the other six are standing at the post their department's stock is
       * at. A tell that walks a gate away from their post breaks the thing the
       * post is for, and it broke the alibi measurement the first time it was
       * tried: the floor lead set off toward the office in the middle of a
       * reading of how long somebody keeps you in view.
       */
      const npc = regard.ids.includes(row.id)
        ? npcs.find((candidate) => candidate.data.id === row.id)
        : null;
      if (!npc || !npc.present) continue;
      const shape = reactionOf(npc.data);
      const tell = TELLS[shape];
      if (!tell) continue;

      /*
       * Deciding about something that is not you looks like looking at it.
       *
       * Every tell above is aimed at the player — walking off to the office,
       * coming over, looking pointedly away — and none of them is true of a
       * person working themselves up about a javelina. The one that still is, is
       * going to find a manager; everybody else turns and looks at the thing.
       */
      const about = strongestClaim(row.id);
      /*
       * Deciding about a colleague looks like their own tell, pointed at the
       * colleague — and it ends there. Nobody's card is at stake: the only card
       * in the building is yours, so what they saw a colleague do comes to a
       * let-go the moment they have shown you which kind of person they are.
       */
      const colleague = about?.subject ? npcs.find((other) => other.data.id === about.subject) : null;
      if (colleague) {
        showing.add(row.id);
        npc.say(tell.say, TELL_SECONDS);
        const where = tell.where(npc, colleague.position);
        if (where) npc.goAndLook(where);
        else npc.hold(TELL_SECONDS, { at: colleague.position, away: Boolean(tell.away) });
        fileNoticed({ npc, record: about }, OUTCOMES.LET_GO, shape);
        autosave.touch();
        continue;
      }
      if (about?.subject && shape !== 'complains') {
        showing.add(row.id);
        npc.hold(TELL_SECONDS, { at: about.position });
        continue;
      }

      showing.add(row.id);
      npc.say(tell.say, TELL_SECONDS);
      const where = tell.where(npc);
      if (where) npc.goAndLook(where);
      else npc.hold(TELL_SECONDS, { at: player.position, away: Boolean(tell.away) });
    }
  }

  /**
   * Walking past somebody, and what that tells you about where you stand.
   *
   * The other half of the same problem. Personal standing decides whether a
   * colleague covers for you or reports you, and until now there was no way to
   * read it at all — you found out at the moment it cost you, which is the same
   * fault the reaction shapes had.
   *
   * There is no meter and there is not going to be one. What there is is what you
   * would actually have: somebody either looks up when you go past or they do
   * not. Four steps of the ladder, four things a person does, and the difference
   * between them is legible from twenty feet without a number anywhere.
   *
   * `greets` on the row is the words. The behaviour is here, because it is the
   * same two primitives every tell uses and a data file does not get to move
   * anybody.
   */
  const HELLO = Object.freeze({
    trusted: { seconds: 3, comes: true },
    warm: { seconds: 3, stops: true },
    cold: { seconds: 2 },
    hostile: { seconds: 2, away: true },
  });

  /** Near enough to be walking past somebody rather than across a room from them. */
  const HELLO_REACH = ft(11);
  const HELLO_CLEAR = ft(18);
  /** Who has already looked up, until you have been away long enough to come back. */
  const greeted = new Set();

  function runGreetings() {
    for (const npc of npcs) {
      const id = npc.data.id;
      if (!regard.ids.includes(id)) continue;
      if (!npc.present) {
        greeted.delete(id);
        continue;
      }
      const gap = Math.hypot(
        npc.position.x - player.position.x,
        npc.position.z - player.position.z
      );
      if (gap > HELLO_CLEAR) {
        greeted.delete(id);
        continue;
      }
      if (gap > HELLO_REACH || greeted.has(id)) continue;
      /*
       * Not while they are deciding something about you. A person walking off to
       * find a manager has already told you what they think, and a cheerful
       * hello over the top of it would be two answers to one question.
       */
      if (showing.has(id) || suspicion.stateOf(id) !== CALM) continue;

      greeted.add(id);
      const rung = rungFor(regard.get(id)).id;
      const how = HELLO[rung] ?? HELLO.cold;
      const words = npc.data.greets?.[rung];
      if (words) npc.say(words, how.seconds);

      // Cold is the one that does nothing, which is the whole of what cold is.
      if (how.comes) npc.goAndLook({ ...player.position });
      else if (how.stops) npc.hold(how.seconds, { at: player.position });
      else if (how.away) npc.hold(how.seconds, { at: player.position, away: true });
    }
  }

  /**
   * How long they spend showing you, which is the window you get.
   *
   * Long enough to be seen across a room and to do something about — get out of
   * their eyeline, put the thing down, have a word — and short enough that it is
   * not a stay of execution. The three ways of converting can all still land
   * inside it, which is the point: the tell is a warning and not a pause.
   */
  const TELL_SECONDS = 5;

  /**
   * Extra incidents the one shape with a number on it will hold, by the step of
   * the ladder they are on.
   *
   * Expressed in steps rather than as a slope for the same reason every other
   * threshold in this game is: four states are learnable across a week and a
   * coefficient is only ever noticed afterwards.
   */
  const PATIENCE = Object.freeze({ hostile: -2, cold: 0, warm: 1, trusted: 2 });

  function patienceShift(id) {
    if (!regard.ids.includes(id)) return 0;
    return PATIENCE[rungFor(regard.get(id)).id] ?? 0;
  }

  /**
   * Somebody saw it and said nothing, and the record says which kind of nothing.
   *
   * `reason` carries the reaction rather than a write-up reason, which is the one
   * liberty taken with that field and it pays for itself twice: the after-action
   * record can tell "he covered for you" from "she is keeping count", and the
   * count above is free.
   */
  function letItGo(accusation, shape) {
    fileNoticed(accusation, OUTCOMES.LET_GO, shape);
    if (shape === 'squares-it') {
      /*
       * Covered, and written down. The only reaction that goes into the ledger
       * rather than onto your card — she is square with you now and will not be
       * later, on her own schedule, exactly like every other debt in this game.
       */
      favors.owe({
        staffId: accusation.npc.data.id,
        department: accusation.npc.data.department,
        day: clock.day,
        coolDays: 1,
        weight: 1,
        note: 'Did not see a thing',
      });
    }
    autosave.touch();
    return null;
  }

  /**
   * The paper.
   *
   * The record is written first, and that ordering is load-bearing: a third
   * write-up moves the calendar, and a row written after that lands in a day the
   * player was not there for. Which means asking the file what it is going to do
   * rather than watching what it did — `armed` is the same question `add` asks
   * itself, and an offence still inside its cooling-off period is somebody
   * speaking up and nothing landing.
   */
  function fileIt(accusation) {
    const reason = REASON_FOR[accusation.record.what] ?? REASONS.REPORTED;
    const lands = writeups.armed(reason, deps.elapsed() * 1000);
    fileNoticed(accusation, lands ? OUTCOMES.FILED : OUTCOMES.NAMED, reason);
    // The department of whoever spoke up does not get to think better of you for
    // having spoken up.
    writeUp(reason, accusation.record.detail ?? accusation.npc.data.name, {
      by: accusation.npc.data.department,
    });
    denounce(accusation);
    return null;
  }

  /**
   * Writes what came of an accusation onto the player's copy.
   *
   * Takes the same record the accusation was made on, so the line in the summary
   * and the belief the witness acted on are the same claim and cannot drift into
   * being two accounts of one moment.
   */
  function fileNoticed(accusation, outcome, reason) {
    noticed.file(accusation.record, {
      outcome,
      reason,
      day: clock.day,
      minutes: clock.minutes,
      nameOf,
    });
  }

  /**
   * The price of being accused of helping yourself.
   *
   * Kept here rather than in procurement because procurement no longer knows
   * whether anybody was looking — it sells you the goods and the department finds
   * out or does not. What the department does when somebody names you is the
   * price list's business, which is why the floor comes off the same row that
   * says whether asking is allowed.
   */
  function denounce(accusation) {
    if (accusation.record.what !== 'take') return;
    const floor = procurement.priceOfBeingNamed(accusation.npc.data.id);
    if (floor === null) return;
    standing.floorTo(accusation.npc.data.department, floor, {
      reason: `Named by ${whom(accusation.npc.data.name)}`,
    });
  }

  /**
   * You named somebody as the one feeding the man from the ownership group,
   * and it was not them.
   *
   * They take it by their shape, played out socially: the one who files goes
   * and complains about you, the tellers tell people, the one who squares
   * things writes a debt, and the ones who cover or keep count keep it to
   * themselves. **Never paper, for any shape** (D's ruling): there is no
   * record to act on, because nobody saw anything, it is what you said to
   * their face. So a complaint here is Management's opinion and nothing else,
   * and it does not go through `complain`, whose low-Management branch writes
   * you up. The drop in their own opinion is the caller's, and comes first.
   */
  function wronged(data) {
    const shape = reactionOf(data);
    if (data.says?.accused) npcs.find((npc) => npc.data.id === data.id)?.say(data.says.accused, 6);
    switch (shape) {
      case 'files':
      case 'complains':
        standing.apply('management', COMPLAINT_COST, { reason: `${data.name} went to a manager about you` });
        break;
      case 'tells-crew':
        standing.applyDirect({ [data.department]: -CREW_WORD }, { reason: `${data.name} told the section` });
        break;
      case 'tells-everyone': {
        const word = Object.fromEntries(
          standing.ids.filter((id) => id !== data.department).map((id) => [id, -CREW_WORD])
        );
        standing.applyDirect(word, { reason: `${data.name} told everybody what you said` });
        break;
      }
      case 'squares-it':
        favors.owe({
          staffId: data.id,
          department: data.department,
          day: clock.day,
          coolDays: 1,
          weight: 1,
          note: 'You said it was me',
        });
        break;
      default:
        // Covers and remembers: nothing passed on. What they think of you is
        // the whole of it, and for the one who keeps count that is patience.
        break;
    }
    autosave.touch();
    return shape;
  }

  return {
    chased,
    conductNow,
    wronged,
    runSurveillance,
    reactionOf,
    think,
    TELLS,
    facingOf,
    patienceShift,
    // For the tests, which drive a reaction without a frame loop.
    react,
    strongestClaim,
    complain,
    get showing() {
      return showing;
    },
  };
}
